import { useState } from 'react';
import { authFetch, API } from './api';
import { renderMarkdown } from './ChatWidget';

/**
 * Ideal Property Profile panel — reusable across BuyerManagement (modal)
 * and BuyerCriteria (inline card). Fetches GET /api/insights/ideal-property/:id
 * and renders the resulting markdown narrative with Copy/Regenerate actions.
 *
 * The final line of the profile is expected to be **Search brief:** ... — when
 * present, we split it out and render it in a cyan-bordered call-out box.
 */
export default function IdealProfilePanel({ buyerId, autoGenerate = false }) {
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [clientName, setClientName] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [generated, setGenerated] = useState(autoGenerate);

  const generate = async () => {
    setLoading(true);
    setError('');
    setCopied(false);
    try {
      const res = await authFetch(`${API}/api/insights/ideal-property/${buyerId}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Request failed (${res.status})`);
      }
      const data = await res.json();
      setProfile(data.profile || '');
      setClientName(data.client_name || '');
      setGenerated(true);
    } catch (e) {
      setError(e.message || 'Could not generate profile.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!profile) return;
    try {
      await navigator.clipboard.writeText(profile);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = profile;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Split the search brief off the bottom of the profile if present
  const splitProfile = (text) => {
    if (!text) return { body: '', brief: '' };
    const re = /\*\*Search brief:\*\*\s*([\s\S]+?)\s*$/i;
    const m = text.match(re);
    if (m) {
      return {
        body: text.slice(0, m.index).trim(),
        brief: m[1].trim(),
      };
    }
    return { body: text, brief: '' };
  };

  // Initial state — Generate button only
  if (!generated && !loading) {
    return (
      <div className="bg-[#1A2744] rounded-lg border-l-4 border-[#FF6B35] border border-[#546E7A]/30 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-white text-lg font-semibold flex items-center gap-2">
              🎯 Ideal Property Profile
            </h3>
            <p className="text-gray-400 text-sm mt-1">
              AI-generated narrative of this client's ideal commercial property based on their criteria and scoring history.
            </p>
          </div>
          <button
            onClick={generate}
            className="px-4 py-2 bg-[#FF6B35] text-white text-sm font-semibold rounded-lg hover:brightness-110 transition-all"
          >
            Generate Profile
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-[#1A2744] rounded-lg border-l-4 border-[#FF6B35] border border-[#546E7A]/30 p-5">
        <h3 className="text-white text-lg font-semibold flex items-center gap-2 mb-3">
          🎯 Ideal Property Profile
        </h3>
        <div className="flex items-center gap-2 text-[#00D4FF] text-sm">
          <span className="inline-flex gap-0.5">
            <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
            <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
            <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
          </span>
          <span>Generating profile…</span>
        </div>
      </div>
    );
  }

  const { body, brief } = splitProfile(profile);

  return (
    <div className="bg-[#1A2744] rounded-lg border-l-4 border-[#FF6B35] border border-[#546E7A]/30 p-5">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="text-white text-lg font-semibold flex items-center gap-2">
          🎯 Ideal Property Profile
          {clientName && (
            <span className="text-gray-400 text-sm font-normal">— {clientName}</span>
          )}
        </h3>
        <div className="flex gap-2">
          <button
            onClick={copyToClipboard}
            disabled={!profile}
            className="px-3 py-1.5 bg-[#243B55] text-[#00D4FF] text-sm font-medium rounded-lg hover:bg-[#2d4a6a] transition-colors disabled:opacity-40"
          >
            {copied ? '✓ Copied' : 'Copy'}
          </button>
          <button
            onClick={generate}
            className="px-3 py-1.5 bg-[#243B55] text-[#00E676] text-sm font-medium rounded-lg hover:bg-[#2d4a6a] transition-colors"
          >
            Regenerate
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 text-sm text-red-300 bg-red-900/20 border border-red-500/30 rounded">
          {error}
        </div>
      )}

      {profile && (
        <>
          <div className="text-white text-sm leading-relaxed space-y-2">
            {renderMarkdown(body)}
          </div>

          {brief && (
            <div className="mt-4 px-4 py-3 rounded-lg border bg-[#0A0E27]"
                 style={{ borderColor: 'rgba(0,212,255,0.5)' }}>
              <div className="text-xs uppercase tracking-wider text-[#00D4FF] font-semibold mb-1">
                Search Brief
              </div>
              <div className="text-white text-sm">{brief}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
